import { useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { Input, Select } from 'components/atoms';
import { AddButton } from 'components/atoms/AddButton/AddButton';
import CatalogSelect from 'components/molecules/CatalogSelect/CatalogSelect';
import { paper } from 'utils/assets';
import { SalaryBase } from 'utils/helpers/adminPageConstants';
import { Numbers, catalogoPersonal } from 'utils/helpers/dictionaries';
import { formatValueForDisplay, removeDots } from 'utils/helpers/formatValueForDisplay';

import { IAdminPanelForm } from '../AdminPanelForm.schema';
import { useLaboralSubmit } from '../useLaboralSubmit';

import styles from './CompensationSection.module.scss';

const PayrollOptions = [
  'Salario básico',
  'Salario Integral',
  'Patrocinio Empleador Pensión',
  'Tarjeta de Alimentación',
  'Apoyo de Sostenimiento',
];

export default function CompensationSection() {
  const { control } = useFormContext<IAdminPanelForm>();
  const submitData = useLaboralSubmit();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'compensationInformation',
  });

  useEffect(() => {
    if (fields.length === 0) {
      appendField();
    }
  }, [fields]);

  function appendField() {
    append({
      payrollComponent: '',
      currency: '',
      id: 9999,
      position: 0,
      salaryBase: '',
    });
  }

  function onDelete(id: number) {
    remove(id);
    submitData();
  }

  return (
    <div className={styles.CompensationSection}>
      <h2 className={styles.CompensationSection__Title}>Información de compensación</h2>
      {fields.map((field, index) => (
        <div key={index} className={styles.CompensationSection__Grid}>
          <Controller
            control={control}
            name={`compensationInformation.${index}.payrollComponent` as const}
            render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
              <Select
                className={styles.CompensationSection__PayrollComponent}
                options={PayrollOptions.map((value) => ({
                  value,
                  label: value,
                }))}
                value={{
                  value,
                  label: value,
                }}
                handleChange={(value) => {
                  onChange(value ?? '');
                  submitData();
                }}
                error={error !== undefined}
                textLabel='Componente de nómina'
                htmlForLabel='Componente de nómina'
                subtext={error?.message}
                {...field}
              />
            )}
          />
          <div className={styles.CompensationSection__Salary}>
            <Controller
              control={control}
              name={`compensationInformation.${index}.salaryBase` as const}
              render={({ field: { onChange, onBlur, value, ...rest }, fieldState }) => (
                <Input
                  id={`compensationInformation.${index}.salaryBase` as const}
                  label={SalaryBase.textLabel}
                  error={fieldState.error !== undefined}
                  subtext={fieldState.error?.message}
                  success={fieldState.isTouched && fieldState.error === undefined}
                  maxLength={Numbers.fourty}
                  onChange={(e) => {
                    const inputValue = e.target.value;
                    const valueWithoutDots = removeDots(inputValue);
                    onChange(valueWithoutDots ?? '');
                  }}
                  onBlur={() => {
                    submitData();
                    onBlur();
                  }}
                  value={value !== undefined ? formatValueForDisplay(value) : ''}
                  {...rest}
                />
              )}
            />
          </div>
          <Controller
            control={control}
            name={`compensationInformation.${index}.currency` as const}
            render={({ field: { onChange, value, name, ...field }, fieldState: { error } }) => (
              <CatalogSelect
                catalog={catalogoPersonal.currency}
                value={{
                  value,
                  label: value,
                }}
                handleChange={(value) => {
                  onChange(value ?? '');
                  submitData();
                }}
                error={error !== undefined}
                textLabel='Moneda'
                htmlForLabel='Moneda'
                subtext={error?.message}
                name={name}
                {...field}
              />
            )}
          />
          {index !== 0 && (
            <div className={styles.CompensationSection__RemoveButtonContainer}>
              <img src={paper} alt='Eliminar' onClick={() => onDelete(index)} />
            </div>
          )}
          {index === fields.length - 1 && (
            <div className={styles.CompensationSection__AddButtonContainer}>
              <AddButton onClick={() => appendField()} expanded withAnimation={false} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
